import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useImportsAddAndSyncImportSource } from "src/model/imports";
import LoadingOverlay from "src/components/LoadingOverlay";
import { Broker } from "src/model_v2/client.schemas";
import { safeStorage } from "src/util/safe_storage";

export const Route = createFileRoute("/oauth/callback")({
  component: OAuthCallbackPage,
  staticData: {
    title: "Connecting to Broker",
  },
});

function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { mutate: addAndSyncImportSource } = useImportsAddAndSyncImportSource();

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const error = urlParams.get("error");

        // Check for errors from the OAuth provider
        if (error) {
          setError(`Authentication error: ${error}`);
          setTimeout(() => navigate({ to: "/import" }), 2000);
          return;
        }

        // Validate required parameters
        if (!code) {
          setError("Missing required parameters");
          setTimeout(() => navigate({ to: "/import" }), 2000);
          return;
        }

        // Get stored broker and account information
        const brokerResult = safeStorage.getItem("oauth_broker");
        const accountResult = safeStorage.getItem("oauth_account");
        const startDateResult = safeStorage.getItem("oauth_start_date");
        const isDemoResult = safeStorage.getItem("oauth_is_demo");

        const broker = brokerResult.success ? (brokerResult.data as Broker) : null;
        const account = accountResult.success ? (accountResult.data || "") : "";
        const startDateStr = startDateResult.success ? startDateResult.data : null;
        const isDemo = isDemoResult.success && isDemoResult.data === "true";

        if (!broker || !account) {
          setError("Missing broker or account information");
          setTimeout(() => navigate({ to: "/import" }), 3000);
          return;
        }

        // Send message to opener window if available
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "OAUTH_CALLBACK",
              payload: {
                code,
                broker,
                account,
                startDate: startDateStr || undefined,
                is_demo: isDemo,
              },
            },
            window.location.origin,
          );

          // Clear OAuth data from localStorage
          safeStorage.removeItem("oauth_state");
          safeStorage.removeItem("oauth_broker");
          safeStorage.removeItem("oauth_account");
          safeStorage.removeItem("oauth_start_date");
          safeStorage.removeItem("oauth_is_demo");

          // Close this window after a short delay
          setTimeout(() => window.close(), 1000);
          return;
        }
      } catch (err) {
        setError(`An unexpected error occurred: ${err}`);
        setTimeout(() => navigate({ to: "/import" }), 3000);
      }
    };

    processOAuthCallback();
  }, [navigate, addAndSyncImportSource]);

  if (error) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Connection Error</h1>
          <p className="mb-6 text-destructive">{error}</p>
          <p>Redirecting back to import page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center">
      <LoadingOverlay isLoading={true} />
      <div className="text-center">
        <h1 className="mb-4 text-2xl font-bold">Connecting to Broker</h1>
        <p className="mb-6">
          Please wait while we establish a connection to your broker...
        </p>
        {window.opener && (
          <p className="text-sm text-muted-foreground">
            This window will close automatically once the connection is
            established.
          </p>
        )}
      </div>
    </div>
  );
}
